import { CompanyLogo } from "~/domains/company/components/CompanyLogo";
import { useIsMutating } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useCommerceContext, useSessionUser } from "~/app/config/useSession";
import { userIsPublic } from "~/domains/user/utils/roles";
import { useForcedConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import {
  ConfiguratorHeaderView,
  type ConfiguratorConfiguration,
  configuratorMutationKey,
  notifyConfiguratorNavigationReset,
  useConfiguratorSave,
  useTemporaryConfiguratorReset,
} from "~/features/configurator-v2";
import type { ConfiguratorRuntimeContext } from "~/features/configurator-v2/common/contract/context";
import { AuthOverlayHost } from "~/features/auth/AuthOverlayHost";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";
import { HeaderLoginElement } from "~/features/navigation/components/TopBar/HeaderLoginElement";
import { UserNavigation } from "~/features/navigation/components/TopBar/UserNavigation";
import { LanguageSelector } from "~/features/categoryNavigation/components/LanguageSelector";
import { SAVED_CONFIGS_PAGE } from "~/utils/url/staticUrls";

interface ConfiguratorHeaderProps {
  configuration: ConfiguratorConfiguration;
  context: ConfiguratorRuntimeContext;
  complete: boolean;
  titleKey: string;
}

export const getConfiguratorOpenUrl = (publicUser: boolean, currentUrl: string) => {
  if (publicUser) {
    return undefined;
  }

  const params = new URLSearchParams({ from: currentUrl });
  return `${SAVED_CONFIGS_PAGE}?${params.toString()}`;
};

export const ConfiguratorHeader = ({ configuration, context, complete, titleKey }: ConfiguratorHeaderProps) => {
  const dispatch = useDispatch();
  const publicUser = userIsPublic();
  const sessionUser = useSessionUser();
  const selectedAccount = useCommerceContext()?.selectedAccount;
  const { t: configuratorT } = useForcedConfiguratorTranslations(configuration.productCode);
  const reset = useTemporaryConfiguratorReset(configuration);
  const save = useConfiguratorSave(configuration, complete);
  const configurationMutationsPending =
    useIsMutating({ mutationKey: configuratorMutationKey(configuration.productCode) }) > 0;
  const translatedTitle = configuratorT?.(titleKey, { defaultValue: titleKey });
  const title = translatedTitle?.trim() || titleKey;
  const openUrl = getConfiguratorOpenUrl(publicUser, window.location.href);
  const saveDisabledReason =
    !publicUser && context.saveMode === "COMPLETE" && !complete
      ? configuratorT?.("page.linear.saveConfig.incomplete", {
          defaultValue: "Complete the configuration before saving",
        }) || "Complete the configuration before saving"
      : null;

  return (
    <>
      <ConfiguratorHeaderView
        title={title}
        helpUrl={configuration.helpUrl ?? null}
        languageSelector={<LanguageSelector />}
        accountNavigation={
          publicUser ? (
            <HeaderLoginElement />
          ) : sessionUser ? (
            <UserNavigation user={{ ...sessionUser, selectedAccount }} showEdit={false} />
          ) : null
        }
        logo={<CompanyLogo />}
        openUrl={openUrl}
        onOpen={publicUser ? () => dispatch(openAuthOverlay("register")) : undefined}
        showSave={context.customization.showSave}
        actionsDisabled={configurationMutationsPending}
        saveDisabledReason={saveDisabledReason}
        savePending={save.isSaving}
        saveError={
          save.error
            ? configuratorT?.("page.linear.saveConfig.result.failure", {
                defaultValue: "Saving the configuration failed.",
              }) || "Saving the configuration failed."
            : null
        }
        onSaveRequested={publicUser ? () => dispatch(openAuthOverlay("register")) : undefined}
        onBack={() => window.history.back()}
        onReset={async () => {
          await reset.resetConfiguration();
          notifyConfiguratorNavigationReset();
        }}
        onSave={publicUser ? undefined : (name) => save.saveConfiguration(name).then(() => undefined)}
      />
      {publicUser && <AuthOverlayHost />}
    </>
  );
};
